import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Spinner } from "@/components/common";
import { AdminLayout } from "@/pages/admin/adminShared";
import { formatDateTime } from "@/lib/format";
import { ShieldCheck, Megaphone, Send, UserCog, ArrowRight } from "lucide-react";

const PERM_LABELS = { announce_manage: "Announcements", sms_send: "Emergency SMS", safety_access: "Safety access" };
const PERM_ICON = { announce_manage: Megaphone, sms_send: Send, safety_access: ShieldCheck };

function RoleBadge({ role }) {
  return <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold capitalize text-[#5C4A42]">{role || "—"}</span>;
}

export default function PermissionAudit() {
  const { data: rows, isLoading } = useQuery({ queryKey: ["permission-audit"], queryFn: async () => (await api.get("/admin/permission-audit")).data });

  return (
    <AdminLayout title="Permission Audit" subtitle="A complete record of every permission grant/revoke and role change. UK date and time (Europe/London).">
      {isLoading ? <Spinner /> : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-[#6E5B51]"><tr>
              <th className="p-3">Date &amp; time</th><th className="p-3">Administrator</th>
              <th className="p-3">Member affected</th><th className="p-3">Change</th><th className="p-3">Action</th>
            </tr></thead>
            <tbody>
              {(rows || []).map((r) => {
                const isRole = r.type === "role" || r.permission === "role";
                const Icon = isRole ? UserCog : (PERM_ICON[r.permission] || ShieldCheck);
                return (
                  <tr key={r.id} className="border-b border-border last:border-0" data-testid={`audit-row-${r.id}`}>
                    <td className="p-3 text-[#5C4A42]">{formatDateTime(r.at)}</td>
                    <td className="p-3 font-semibold text-[#2D1E18]">{r.actor_name}</td>
                    <td className="p-3 text-[#2D1E18]">{r.target_name}</td>
                    <td className="p-3"><span className="inline-flex items-center gap-1 text-[#5C4A42]"><Icon size={13} /> {isRole ? "Role" : (PERM_LABELS[r.permission] || r.permission)}</span></td>
                    <td className="p-3">
                      {isRole ? (
                        <span className="inline-flex items-center gap-1.5" data-testid={`audit-role-change-${r.id}`}>
                          <RoleBadge role={r.old_role} /> <ArrowRight size={12} className="text-[#6E5B51]" /> <RoleBadge role={r.new_role} />
                        </span>
                      ) : (
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${r.action === "grant" ? "bg-[#E8F0EC] text-[#4A6B5D]" : "bg-[#FBE9E4] text-[#B04B26]"}`}>
                          {r.action === "grant" ? "Granted" : "Revoked"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {(!rows || rows.length === 0) && <tr><td colSpan={5} className="p-6 text-center text-sm text-[#6E5B51]" data-testid="audit-empty">No changes recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
